import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import IRider, { IRiderDetail, IRiderExtended } from '../../models/IRider';
import RiderServiceInstance from '../../services/RiderService';
import { Grid, Theme, Paper } from '@mui/material';
import { makeStyles } from '@mui/styles';
import createStyles from '@mui/styles/createStyles';
import ParticipantChart from './ParticipantChart';
import LapsHelperInstance from '../../services/LapsHelper';
import ParticipantSummary from './ParticipantSummary';
import ParticipantLaps from './ParticipantLaps';
import OntimeBreadcrumb from '../common/OntimeBreadcrumb';
import * as signalR from "@microsoft/signalr";
import { signalRTimingHubUrl } from '../../services/ServiceConstants';
import UserServiceInstance from '../../services/UserService';
import { useSnackbar } from 'notistack';

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        table: {
            // minWidth: 650,
            tableLayout: 'auto'
        },
        container: {
            paddingTop: theme.spacing(10),
            paddingBottom: theme.spacing(4),
            paddingLeft: theme.spacing(2),
            paddingRight: theme.spacing(2)
        },
        paper: {
            padding: theme.spacing(2),
            display: 'flex',
            overflow: 'auto',
            flexDirection: 'column',
        },
        chartContainer: {
            height: '400px',
            padding: theme.spacing(2),
            display: 'flex',
            overflow: 'auto',
            flexDirection: 'column',
        },
        summaryContainer: {
            marginTop: theme.spacing(3), // Adjust the value as needed
        }
    }));

function ParticipantDetail() {
    const { eventId, tagId } = useParams();
    const [rider, setRider] = useState<IRiderDetail>();
    const hubConnectionRef = useRef<signalR.HubConnection>();
    const snackBar = useSnackbar();
    const classes = useStyles();

    const fetchRider = useCallback(async (): Promise<IRiderDetail> => {
        if (!eventId) {
            throw new Error('EventId is required');
        }
        if (!tagId) {
            throw new Error('TagId is required');
        }
        console.log('tagid ' + tagId);
        const rider = tagId
            ? await RiderServiceInstance.getRiderDetail(eventId, tagId)
            : await UserServiceInstance.getUserParticipation(eventId);
        return rider;
    }, [eventId, tagId]);

    const fetchRiderDetailAndSubscribeSignalR = useCallback(async (rider: IRiderDetail, hc: signalR.HubConnection) => {
        if (rider.liveEvent === true) {
            hubConnectionRef.current = hc;
            try {
                await hc.start();
                if (hc.connectionId) {
                    const participantId = `${eventId}--${rider.tagIdFriendly}`
                    hc.invoke("JoinRace", participantId);
                }

                hc.on("publishTime", (publishedRider: IRider) => {
                    if (!eventId) {
                        throw new Error('EventId is required');
                    }
                    RiderServiceInstance.getRiderRefresh(eventId, publishedRider.tagIdFriendly).then((riderExtended: IRiderExtended) => {
                        if (publishedRider) {
                            let mergedRider = { ...rider, ...publishedRider, ...riderExtended };
                            setRider(mergedRider);
                        }
                    });
                });
            } catch (error) {
                console.error("Error starting SignalR connection: ", error);
                // todo: localize message
                snackBar.enqueueSnackbar("Error starting real-time lap updates", { variant: 'error' });
            }
        }
    }, [eventId, snackBar]);

    const unsubscribeSignalR = useCallback(() => {
        hubConnectionRef.current?.stop().then(() => console.log("participant connection closed"));
    }, []);

    useEffect(() => {
        fetchRider().then(rider => {
            setRider(rider);
            console.debug(rider);
            if (rider.liveEvent === true) {
                // Builds the SignalR connection, mapping it to /timing
                const hubConnection = new signalR.HubConnectionBuilder()
                    .withUrl(signalRTimingHubUrl)
                    .withAutomaticReconnect()
                    .configureLogging(signalR.LogLevel.Information)
                    .build();

                fetchRiderDetailAndSubscribeSignalR(rider, hubConnection)
                    .catch(reason => snackBar.enqueueSnackbar(reason, { variant: 'error' }));
            }
        });
    }, [fetchRider, fetchRiderDetailAndSubscribeSignalR, snackBar]);

    useEffect(() => {
        window.addEventListener('unload', unsubscribeSignalR);
        return () => {
            window.removeEventListener('unload', unsubscribeSignalR);
            unsubscribeSignalR();
        }
    }, [unsubscribeSignalR]);


    const laps = rider?.laps ?? [];
    const idBestLap = rider?.bestLapId ?? 0;
    const bestLap = LapsHelperInstance.getBestLap(laps);
    const filteredLaps = LapsHelperInstance.filterBreakLaps(laps, bestLap?.timeMs);

    const eventName = rider?.eventName ?? '';
    const riderName = rider?.name ?? '';
    const eventRoute = `/event/${eventId}`;
    let nameMap: Record<string, string> = {};
    nameMap[eventName] = eventRoute;

    return (
        <Grid container className={classes.container} spacing={2} >
            <Grid item xs={12}>
                <OntimeBreadcrumb nameMap={nameMap} current={riderName} />
            </Grid>
            <Grid item xs={12} className={classes.summaryContainer}>
                <Paper>
                    <ParticipantSummary rider={rider} />
                </Paper>
            </Grid>
            <Grid item xs={12}>
                <ParticipantLaps laps={filteredLaps} idBestLap={idBestLap} />
            </Grid>
            <Grid item xs={12}>
                <Paper className={classes.chartContainer}>
                    <ParticipantChart
                        laps={filteredLaps}
                        bestLap={bestLap}
                        bestGroup={rider?.groupBest}
                        bestToday={rider?.eventBest}
                        bestCircuit={rider?.circuitBest}
                        averageTime={rider?.averageMs} />
                </Paper>
            </Grid>
        </Grid>
    );
}

export default ParticipantDetail;